// EquipmentInspectionListView.tsx
// Shows all inspections for a specific equipment type + premises + month
// with full checklist detail and PDF download
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { Download, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertTriangle, X } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../common/axiosInstance";
import { DotLoader } from "../../components/loader/Loader";
import { usePremisesStore } from "../../store/store";
import { getPremiseName, getMonthName } from "./constants";

interface InspectionResponse {
  itemId: string;
  question?: string;
  value?: any;
  status: string;
  comment?: string;
}

interface Inspection {
  id: string;
  assetId: string;
  assetName?: string;
  assetLocation?: string;
  assetFloor?: string;
  equipmentInventoryName?: string;
  inspectorName: string;
  inspectorEmail?: string;
  inspectionDate: string;
  inspectionType: string;
  overallStatus: "pass" | "fail" | "attention";
  overallComments?: string;
  correctiveActions?: string;
  nextInspectionDate?: string;
  responses: InspectionResponse[];
  checklistName?: string;
  createdAt: string;
}

const fmt = (d: string) => {
  if (!d) return "N/A";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "N/A"; }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  if (s === "pass") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Pass</span>;
  if (s === "fail") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800"><XCircle className="w-3 h-3" />Fail</span>;
  if (s === "attention") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3" />Attention</span>;
  return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{status || "N/A"}</span>;
};

const ResponseStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  if (s === "pass") return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
  if (s === "fail") return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (s === "attention") return <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />;
  return <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />;
};

const getResponseStatusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "pass") return "bg-green-50 border-green-200";
  if (s === "fail") return "bg-red-50 border-red-200";
  if (s === "attention") return "bg-yellow-50 border-yellow-200";
  return "bg-gray-50 border-gray-200";
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const InspectionDetailModal: React.FC<{ inspection: Inspection; onClose: () => void; onDownload: () => void }> = ({ inspection, onClose, onDownload }) => {
  const passCount = inspection.responses.filter(r => r.status?.toLowerCase() === "pass").length;
  const failCount = inspection.responses.filter(r => r.status?.toLowerCase() === "fail").length;
  const attentionCount = inspection.responses.filter(r => r.status?.toLowerCase() === "attention").length;
  const naCount = inspection.responses.filter(r => r.status?.toLowerCase() === "na").length;
  const total = inspection.responses.filter(r => r.status && r.status.toLowerCase() !== "na").length;
  const compliancePct = total > 0 ? Math.round((passCount / total) * 100) : 0;

  useEffect(() => {
    // Move windows on top  
    // Unexpected use of 'screen' 
    window.moveTo(0, 0);
  }, [])

  return (
    <div className="w-full">
    {/* //  className="fixed inset-0 z-50 bg-black/60 flex flex-col"> */}
      {/* Header */}
      <div className="bg-white border-b border-gray-200 mt-16 px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            ← Back to list
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-800">Inspection Detail</span>
          <StatusBadge status={inspection.overallStatus} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onDownload} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium">
            <Download className="w-4 h-4" />Download PDF
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto space-y-5">
          {/* Asset + Inspector info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Inspection Report</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Asset</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{inspection.assetName || inspection.assetId}</p></div>
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Equipment Type</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{inspection.equipmentInventoryName || "N/A"}</p></div>
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Location</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{inspection.assetLocation || "N/A"}{inspection.assetFloor ? ` · ${inspection.assetFloor}` : ""}</p></div>
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Inspector</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{inspection.inspectorName}</p></div>
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Inspection Date</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{fmt(inspection.inspectionDate)}</p></div>
              <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Type</span><p className="text-gray-800 font-medium mt-0.5 text-sm capitalize">{inspection.inspectionType}</p></div>
              {inspection.checklistName && <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Checklist</span><p className="text-[#00A8A5] font-medium mt-0.5 text-sm">{inspection.checklistName}</p></div>}
              {inspection.nextInspectionDate && <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Next Inspection</span><p className="text-gray-800 font-medium mt-0.5 text-sm">{fmt(inspection.nextInspectionDate)}</p></div>}
            </div>
          </div>

          {/* Compliance summary */}
          {inspection.responses.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-800">Compliance Summary</h3>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${compliancePct >= 80 ? "bg-green-100 text-green-800" : compliancePct >= 60 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                  {compliancePct}% Complied
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: "Pass", count: passCount, color: "bg-green-50 border-green-200 text-green-700", icon: <CheckCircle className="w-4 h-4" /> },
                  { label: "Attention", count: attentionCount, color: "bg-yellow-50 border-yellow-200 text-yellow-700", icon: <AlertTriangle className="w-4 h-4" /> },
                  { label: "Fail", count: failCount, color: "bg-red-50 border-red-200 text-red-700", icon: <XCircle className="w-4 h-4" /> },
                  { label: "N/A", count: naCount, color: "bg-gray-50 border-gray-200 text-gray-600", icon: <div className="w-4 h-4 rounded-full border-2 border-gray-400" /> },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl border p-3 flex items-center gap-3 ${c.color}`}>
                    {c.icon}
                    <div><div className="text-xl font-bold">{c.count}</div><div className="text-xs font-medium">{c.label}</div></div>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div className="h-2 rounded-full overflow-hidden bg-gray-100 flex">
                <div className="h-full bg-green-500" style={{ width: `${inspection.responses.length > 0 ? (passCount / inspection.responses.length) * 100 : 0}%` }} />
                <div className="h-full bg-yellow-400" style={{ width: `${inspection.responses.length > 0 ? (attentionCount / inspection.responses.length) * 100 : 0}%` }} />
                <div className="h-full bg-red-500" style={{ width: `${inspection.responses.length > 0 ? (failCount / inspection.responses.length) * 100 : 0}%` }} />
              </div>
            </div>
          )}

          {/* Checklist items */}
          {inspection.responses.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-800">Inspection Checklist ({inspection.responses.length} items)</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {inspection.responses.map((r, idx) => (
                  <div key={`${r.itemId}-${idx}`} className={`px-6 py-4 flex items-start gap-3 ${getResponseStatusColor(r.status)} border-l-4`}>
                    <ResponseStatusIcon status={r.status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 leading-relaxed">
                        {r.question || r.itemId?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      {r.value !== undefined && r.value !== null && r.value !== "" && (
                        <p className="text-xs text-gray-500 mt-1">Response: <span className="font-medium text-gray-700">{String(r.value)}</span></p>
                      )}
                      {r.comment && (
                        <p className="text-xs text-gray-500 mt-1 italic bg-white/60 rounded px-2 py-1 inline-block">💬 {r.comment}</p>
                      )}
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 capitalize ${
                      r.status?.toLowerCase() === "pass" ? "bg-green-100 text-green-800" :
                      r.status?.toLowerCase() === "fail" ? "bg-red-100 text-red-800" :
                      r.status?.toLowerCase() === "attention" ? "bg-yellow-100 text-yellow-800" :
                      "bg-gray-100 text-gray-600"
                    }`}>{r.status || "N/A"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Overall comments + corrective actions */}
          {(inspection.overallComments || inspection.correctiveActions) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              {inspection.overallComments && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Overall Comments</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{inspection.overallComments}</p>
                </div>
              )}
              {inspection.correctiveActions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-orange-500" />Corrective Actions</h3>
                  <p className="text-sm text-orange-900 bg-orange-50 border-l-4 border-orange-400 rounded-lg p-3">{inspection.correctiveActions}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EquipmentInspectionListView: React.FC = () => {
  const { premiseId, month, equipType } = useParams<{ premiseId: string; month: string; equipType: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();

  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const premiseName = getPremiseName(premiseId ?? "", allPremises);
  const monthName = getMonthName(Number(month));
  const year = searchParams.get("year") || String(new Date().getFullYear());
  const decodedEquipType = decodeURIComponent(equipType ?? "");
  const isObjectId = (s: string) => /^[a-f0-9]{24}$/i.test(s);

  useEffect(() => { if (!allPremises.length) fetchPremises(); }, []);

  useEffect(() => {
    if (!premiseId || !month || !equipType) return;
    setLoading(true);
    setError(null);
    axiosInstance.get("/api/inspection-assessments/monitoring/by-premises", {
      params: { premisesId: premiseId, month, year, equipmentType: decodedEquipType },
    }).then(r => {
      const data = r.data?.data || r.data;
      setInspections(data?.inspections || []);
    }).catch(e => setError(e.response?.data?.message || "Failed to load inspections"))
      .finally(() => setLoading(false));
  }, [premiseId, month, equipType, year]);

  const filtered = useMemo(() =>
    statusFilter === "all" ? inspections : inspections.filter(i => i.overallStatus === statusFilter),
    [inspections, statusFilter]
  );

  const stats = useMemo(() => ({
    total: inspections.length,
    pass: inspections.filter(i => i.overallStatus === "pass").length,
    fail: inspections.filter(i => i.overallStatus === "fail").length,
    attention: inspections.filter(i => i.overallStatus === "attention").length,
  }), [inspections]);

  const equipTypeName = (() => {
    const raw = inspections[0]?.equipmentInventoryName || decodedEquipType;
    return raw || "Equipment Inspections";
  })();

  const handleDownloadPDF = (insp: Inspection) => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(18); doc.setTextColor(0, 168, 165);
      doc.text("Equipment Inspection Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      doc.text(`Equipment Type: ${insp.equipmentInventoryName || "N/A"}`, 14, 26);
      doc.text(`Asset: ${insp.assetName || insp.assetId}`, 14, 32);
      doc.text(`Location: ${insp.assetLocation || "N/A"}${insp.assetFloor ? ` · ${insp.assetFloor}` : ""}`, 14, 38);
      doc.text(`Premises: ${premiseName}`, 14, 44);
      doc.text(`Inspector: ${insp.inspectorName}`, 14, 50);
      doc.text(`Date: ${fmt(insp.inspectionDate)}`, 14, 56);
      doc.text(`Overall Status: ${insp.overallStatus?.toUpperCase()}`, 14, 62);
      if (insp.checklistName) doc.text(`Checklist: ${insp.checklistName}`, 14, 68);

      const passC = insp.responses.filter(r => r.status?.toLowerCase() === "pass").length;
      const failC = insp.responses.filter(r => r.status?.toLowerCase() === "fail").length;
      const attC = insp.responses.filter(r => r.status?.toLowerCase() === "attention").length;
      const tot = insp.responses.filter(r => r.status && r.status.toLowerCase() !== "na").length;
      doc.text(`Compliance: ${tot > 0 ? Math.round((passC / tot) * 100) : 0}% (Pass: ${passC}, Attention: ${attC}, Fail: ${failC})`, 14, 74);

      if (insp.responses.length > 0) {
        autoTable(doc, {
          startY: 82,
          head: [["#", "Question / Item", "Response", "Status", "Comment"]],
          body: insp.responses.map((r, i) => [
            i + 1,
            r.question || r.itemId?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || r.itemId,
            r.value !== undefined && r.value !== null ? String(r.value) : "—",
            r.status || "N/A",
            r.comment || "—",
          ]),
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [0, 168, 165] },
          columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 90 }, 2: { cellWidth: 25 }, 3: { cellWidth: 22 } },
          didParseCell: (data: any) => {
            if (data.column.index === 3 && data.section === "body") {
              const s = (data.cell.text[0] || "").toLowerCase();
              if (s === "pass") data.cell.styles.textColor = [22, 163, 74];
              else if (s === "fail") data.cell.styles.textColor = [220, 38, 38];
              else if (s === "attention") data.cell.styles.textColor = [202, 138, 4];
            }
          },
        });
      }

      let y = (doc as any).lastAutoTable?.finalY + 8 || 90;
      if (insp.overallComments) {
        doc.setFontSize(10); doc.setTextColor(80, 80, 80);
        doc.text("Overall Comments:", 14, y); y += 6;
        const lines = doc.splitTextToSize(insp.overallComments, doc.internal.pageSize.width - 28);
        doc.text(lines, 14, y); y += lines.length * 5 + 4;
      }
      if (insp.correctiveActions) {
        doc.setTextColor(180, 80, 0);
        doc.text("Corrective Actions:", 14, y); y += 6;
        const lines = doc.splitTextToSize(insp.correctiveActions, doc.internal.pageSize.width - 28);
        doc.text(lines, 14, y);
      }

      doc.save(`Inspection_${(insp.assetName || insp.assetId).replace(/[^a-zA-Z0-9]/g, "_")}_${fmt(insp.inspectionDate).replace(/\s/g, "_")}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      alert("Failed to generate PDF.");
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64 mt-20"><DotLoader /></div>;

  return (
    <>
      {!selectedInspection && <div className="w-full px-6 mt-20 pb-10">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
            <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium">Monitoring</Link></li>
            <li className="text-gray-300">›</li>
            <li><Link to={`/fire-system/monitoring/${premiseId}`} className="hover:text-[#00A8A5] truncate max-w-[100px] inline-block">{premiseName}</Link></li>
            <li className="text-gray-300">›</li>
            <li><Link to={`/fire-system/monitoring/${premiseId}/equipment-inspections`} className="hover:text-[#00A8A5]">Equipment Inspections</Link></li>
            <li className="text-gray-300">›</li>
            <li><Link to={`/fire-system/monitoring/${premiseId}/equipment-inspections/${month}`} className="hover:text-[#00A8A5]">{monthName}</Link></li>
            <li className="text-gray-300">›</li>
            <li aria-current="page" className="text-gray-800 font-semibold truncate max-w-[120px]">{equipTypeName}</li>
          </ol>
        </nav>

        <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}/equipment-inspections/${month}?year=${year}`)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00A8A5] transition-colors mb-5 group">
          <span className="group-hover:-translate-x-0.5 transition-transform">←</span>Back
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-[#00A8A5]/10 rounded-xl flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#00A8A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{equipTypeName}</h1>
            </div>
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-700">{premiseName}</span>
              {" · "}
              <span className="font-semibold text-gray-700">{monthName} {year}</span>
            </p>
          </div>
          {/* Status filter */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {[
              { key: "all", label: `All (${stats.total})` },
              { key: "pass", label: `Pass (${stats.pass})` },
              { key: "attention", label: `Attention (${stats.attention})` },
              { key: "fail", label: `Fail (${stats.fail})` },
            ].map(f => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${statusFilter === f.key ? "bg-white text-[#00A8A5] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total", value: stats.total, color: "text-[#00A8A5]", bg: "bg-[#00A8A5]/10" },
            { label: "Pass", value: stats.pass, color: "text-green-600", bg: "bg-green-50" },
            { label: "Attention", value: stats.attention, color: "text-yellow-600", bg: "bg-yellow-50" },
            { label: "Fail", value: stats.fail, color: "text-red-600", bg: "bg-red-50" },
          ].map(c => (
            <div key={c.label} className={`rounded-xl p-4 ${c.bg} border border-transparent`}>
              <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
              <div className="text-xs text-gray-500 font-medium mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        {/* Inspection cards */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="bg-gray-100 rounded-full p-5 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No inspections found</h3>
            <p className="text-gray-400 text-sm">No {statusFilter !== "all" ? statusFilter : ""} inspections for this equipment type.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filtered.map(insp => {
              const isExpanded = expandedId === insp.id;
              const passC = insp.responses.filter(r => r.status?.toLowerCase() === "pass").length;
              const tot = insp.responses.filter(r => r.status && r.status.toLowerCase() !== "na").length;
              const pct = tot > 0 ? Math.round((passC / tot) * 100) : null;

              return (
                <div key={insp.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Card header */}
                  <div className="px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <StatusBadge status={insp.overallStatus} />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{insp.assetName || insp.assetId}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{insp.assetLocation || "—"}{insp.assetFloor ? ` · ${insp.assetFloor}` : ""} · {fmt(insp.inspectionDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pct !== null && (
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${pct >= 80 ? "bg-green-100 text-green-700" : pct >= 60 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                          {pct}%
                        </span>
                      )}
                      <button onClick={() => setSelectedInspection(insp)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#00A8A5] rounded-lg hover:bg-[#008A87] transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        View
                      </button>
                      <button onClick={() => handleDownloadPDF(insp)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors">
                        <Download className="w-3.5 h-3.5" />PDF
                      </button>
                      <button onClick={() => setExpandedId(isExpanded ? null : insp.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Inspector + checklist info */}
                  <div className="px-5 pb-3 flex flex-wrap gap-4 text-xs text-gray-500 border-t border-gray-50">
                    <span>Inspector: <span className="font-medium text-gray-700">{insp.inspectorName}</span></span>
                    {insp.checklistName && <span>Checklist: <span className="font-medium text-[#00A8A5]">{insp.checklistName}</span></span>}
                    <span>Type: <span className="font-medium text-gray-700 capitalize">{insp.inspectionType}</span></span>
                    {insp.responses.length > 0 && <span>{insp.responses.length} items checked</span>}
                  </div>

                  {/* Expanded checklist preview */}
                  {isExpanded && insp.responses.length > 0 && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {insp.responses.map((r, idx) => (
                        <div key={idx} className={`px-5 py-3 flex items-start gap-3 ${getResponseStatusColor(r.status)} border-l-4`}>
                          <ResponseStatusIcon status={r.status} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800">{r.question || r.itemId?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                            {r.comment && <p className="text-xs text-gray-500 mt-0.5 italic">💬 {r.comment}</p>}
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 capitalize ${
                            r.status?.toLowerCase() === "pass" ? "bg-green-100 text-green-800" :
                            r.status?.toLowerCase() === "fail" ? "bg-red-100 text-red-800" :
                            r.status?.toLowerCase() === "attention" ? "bg-yellow-100 text-yellow-800" :
                            "bg-gray-100 text-gray-600"
                          }`}>{r.status || "N/A"}</span>
                        </div>
                      ))}
                      {insp.overallComments && (
                        <div className="px-5 py-3 bg-gray-50">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Comments</p>
                          <p className="text-sm text-gray-700">{insp.overallComments}</p>
                        </div>
                      )}
                      {insp.correctiveActions && (
                        <div className="px-5 py-3 bg-orange-50 border-l-4 border-orange-400">
                          <p className="text-xs font-semibold text-orange-700 mb-1">⚠ Corrective Actions</p>
                          <p className="text-sm text-orange-900">{insp.correctiveActions}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* Full detail modal */}
      {selectedInspection && (
        <InspectionDetailModal
          inspection={selectedInspection}
          onClose={() => setSelectedInspection(null)}
          onDownload={() => handleDownloadPDF(selectedInspection)}
        />
      )}
    </>
  );
};

export default EquipmentInspectionListView;
